import {
  DEALERSHIP_TIMEZONE,
  STOCK_ORDER_REPORT_MAX_DAYS,
  STOCK_ORDER_SIDES,
  type StockOrderLineStatus,
  type StockOrderReport,
  type StockOrderReportLine,
  type StockOrderSide,
  type StockOrderSideFilter,
} from "../../../../shared/contracts.js";
import {
  addDays,
  daysInclusive,
  endOfMonth,
  endOfWeek,
  isLocalDate,
  localDateOf,
  startOfMonth,
  startOfWeek,
} from "../../../../shared/local-date.js";
import { STATUS_PRESENTATION } from "../../../ui/StatusBadge.js";
import { EXCEPTION_LABELS, etaLabel } from "../parts-actions/VerifyArrivalDialog.js";
import { formatReceiptInstant } from "../receipt/OrderReceiptPage.js";
import { STOCK_ORDER_REPORT_PATH, roLabel } from "../presentation.js";

/**
 * The stock order report's range, its address, and its CSV — the parts of
 * the page that are plain functions (owner request 2026-09-17).
 *
 * The range lives in the URL (`?range=day|week|month|custom&from=&to=`,
 * plus `&side=service|wholesale` when the report is kept to one side of
 * the counter — wholesale spec §8) so a bookmark, a reload or a printed
 * page's address reopens the same report.
 * A preset names its dates relative to *today in the dealership's zone*
 * (`DEALERSHIP_TIMEZONE`, the interface's fixed `America/Chicago` — the
 * same calendar the server bounds the query in): Today is one day, This
 * week runs Monday to Sunday, This month is the calendar month, and Custom
 * carries its two dates. Resolving a preset happens here, in the browser,
 * so the request always names explicit dates and the server never guesses
 * which week the person meant. A custom range is kept to what the server
 * answers — `to` never before `from`, never more than
 * {@link STOCK_ORDER_REPORT_MAX_DAYS} days — by repairing the address on
 * the way in, so a stale or hand-edited bookmark opens a report rather
 * than a refusal.
 *
 * The CSV is built from the loaded report, never fetched again: the rows
 * on screen are the rows in the file. RFC 4180 — CRLF records, a field
 * quoted when it holds a comma, a quote or a line break, quotes doubled —
 * with a UTF-8 byte-order mark in front so Excel reads it as UTF-8 text,
 * and a field that a spreadsheet would run as a formula made inert.
 */

/** `/parts/reports/stock-orders` — declared beside the other Parts destinations. */
export { STOCK_ORDER_REPORT_PATH };

export const RANGE_PRESETS = ["day", "week", "month", "custom"] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number];

export const RANGE_PRESET_LABELS: Readonly<Record<RangePreset, string>> = {
  day: "Today",
  week: "This week",
  month: "This month",
  custom: "Custom",
};

function isRangePreset(value: unknown): value is RangePreset {
  return typeof value === "string" && (RANGE_PRESETS as readonly string[]).includes(value);
}

export type ReportUrlState = Readonly<{
  range: RangePreset;
  /** Only meaningful under `custom`; `null` when absent or not a calendar date. */
  from: string | null;
  to: string | null;
  /** Both sides of the counter (the default), or one. */
  side: StockOrderSideFilter;
}>;

export const REPORT_URL_PARAM = { range: "range", from: "from", to: "to", side: "side" } as const;

/** The Side filter's choices, in the order the bar shows them. */
export const SIDE_FILTERS = ["both", ...STOCK_ORDER_SIDES] as const satisfies readonly StockOrderSideFilter[];

export const SIDE_FILTER_LABELS: Readonly<Record<StockOrderSideFilter, string>> = {
  both: "Both",
  service: "Service",
  wholesale: "Wholesale",
};

/** "Service" / "Wholesale" for a line's side. */
export function sideLabel(side: StockOrderSide): string {
  return SIDE_FILTER_LABELS[side];
}

function isSideFilter(value: unknown): value is StockOrderSideFilter {
  return typeof value === "string" && (SIDE_FILTERS as readonly string[]).includes(value);
}

function dateParam(value: string | null): string | null {
  return value !== null && isLocalDate(value) ? value : null;
}

/** The latest `to` a report starting on `from` may have. */
export function latestEndFor(from: string): string {
  return addDays(from, STOCK_ORDER_REPORT_MAX_DAYS - 1);
}

/** The earliest `from` a report ending on `to` may have. */
export function earliestStartFor(to: string): string {
  return addDays(to, -(STOCK_ORDER_REPORT_MAX_DAYS - 1));
}

/**
 * Two dates the server would answer: swapped when the end came first, and
 * the end pulled back to the longest report when the span ran past it.
 */
function repairDates(from: string, to: string): { from: string; to: string } {
  const [start, end] = to < from ? [to, from] : [from, to];
  return { from: start, to: daysInclusive(start, end) > STOCK_ORDER_REPORT_MAX_DAYS ? latestEndFor(start) : end };
}

/**
 * Unknown presets and malformed dates fall back to Today rather than break
 * the page; a custom range that ends before it starts or runs past the
 * longest report is repaired rather than asked for and refused.
 */
export function parseReportUrlState(params: URLSearchParams): ReportUrlState {
  const range = params.get(REPORT_URL_PARAM.range);
  const from = dateParam(params.get(REPORT_URL_PARAM.from));
  const to = dateParam(params.get(REPORT_URL_PARAM.to));
  const side = params.get(REPORT_URL_PARAM.side);
  const dates = from !== null && to !== null ? repairDates(from, to) : { from, to };
  return {
    range: isRangePreset(range) ? range : "day",
    from: dates.from,
    to: dates.to,
    side: isSideFilter(side) ? side : "both",
  };
}

/** `range` always travels; the dates only under `custom`, and only when set; the side only when narrowed. */
export function serializeReportUrlState(state: ReportUrlState): URLSearchParams {
  const params = new URLSearchParams();
  params.set(REPORT_URL_PARAM.range, state.range);
  if (state.range === "custom") {
    if (state.from) params.set(REPORT_URL_PARAM.from, state.from);
    if (state.to) params.set(REPORT_URL_PARAM.to, state.to);
  }
  if (state.side !== "both") params.set(REPORT_URL_PARAM.side, state.side);
  return params;
}

export type ResolvedRange = Readonly<{ from: string; to: string }>;

/** Today's calendar date at the dealership, from the browser's clock. */
export function todayAtDealership(now: Date = new Date()): string {
  return localDateOf(now, DEALERSHIP_TIMEZONE);
}

/** The inclusive dates a preset means on `today` (`YYYY-MM-DD` at the dealership). */
export function resolveRange(state: Pick<ReportUrlState, "range" | "from" | "to">, today: string): ResolvedRange {
  switch (state.range) {
    case "day":
      return { from: today, to: today };
    case "week":
      return { from: startOfWeek(today), to: endOfWeek(today) };
    case "month":
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case "custom": {
      const from = state.from ?? state.to ?? today;
      return { from, to: state.to ?? from };
    }
  }
}

/** `GET /api/reports/stock-orders?from=…&to=…[&side=…]` for an explicit range; both sides need no `side`. */
export function reportApiPath(range: ResolvedRange, side: StockOrderSideFilter = "both"): string {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  if (side !== "both") params.set("side", side);
  return `/api/reports/stock-orders?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** "Partially arrived", "Backordered": the case status words and the exception words the inspector uses. */
export function lineStatusLabel(status: StockOrderLineStatus): string {
  switch (status) {
    case "ordered":
    case "partially_arrived":
    case "arrived":
      return STATUS_PRESENTATION[status].label;
    default:
      return EXCEPTION_LABELS[status];
  }
}

const localDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Sep 17, 2026" for a calendar date (formatted at noon UTC so no zone can move the day). */
export function formatLocalDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return date;
  return localDateFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}

/** "Sep 14 – Sep 20, 2026", or just the one day. */
export function formatRangeLabel(range: ResolvedRange): string {
  if (range.from === range.to) return formatLocalDate(range.from);
  return `${formatLocalDate(range.from)} – ${formatLocalDate(range.to)}`;
}

export function vehicleLabel(line: Pick<StockOrderReportLine, "vehicle">): string {
  if (line.vehicle === null) return "—";
  return `${line.vehicle.year} ${line.vehicle.make} ${line.vehicle.model}`;
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

const CSV_HEADER = [
  "Ordered at",
  "Part number",
  "Description",
  "Qty ordered",
  "Qty verified",
  "Status",
  "Cancelled",
  "Side",
  "Order",
  "RO",
  "Customer",
  "Vehicle",
  "VIN ending",
  "Advisor",
  "Technician",
  "ETA",
  "Vendor reference",
  "Customer reference",
  "R&R number",
] as const;

/**
 * A cell beginning with one of these is a formula to Excel, LibreOffice and
 * Numbers when they open a .csv, whatever the quoting (OWASP: CSV injection).
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * One CSV field. `null` — an unassigned technician, an order without a VIN
 * — is an empty field, never the word "null". A field is quoted only when
 * it needs to be, a quote inside is doubled (RFC 4180 §2), and one that a
 * spreadsheet would run as a formula is prefixed with an apostrophe (the
 * spreadsheets' own "this is text" mark) and quoted: descriptions and part
 * numbers are typed by people, and a manager double-clicking the file must
 * get the words the report shows, not a live formula.
 */
export function csvField(value: string | number | null): string {
  if (value === null) return "";
  const text = String(value);
  const inert = FORMULA_LEAD.test(text) ? `'${text}` : text;
  return inert !== text || /[",\r\n]/.test(inert) ? `"${inert.replace(/"/g, '""')}"` : inert;
}

function csvRow(fields: readonly (string | number | null)[]): string {
  return `${fields.map(csvField).join(",")}\r\n`;
}

/**
 * The report's lines as one CSV document, BOM first, one record per line,
 * CRLF-terminated. A wholesale line (wholesale spec §8) carries its side,
 * WS number, customer reference and R&R number; its RO, vehicle, VIN and
 * people are empty fields — never "Pre-RO" or "Unassigned", which are
 * service words.
 */
export function stockOrderCsv(report: StockOrderReport): string {
  const rows = report.lines.map((line) =>
    csvRow([
      formatReceiptInstant(line.orderedAt),
      line.partNumber,
      line.description,
      line.quantityOrdered,
      line.quantityVerified,
      lineStatusLabel(line.status),
      line.cancelled ? "Yes" : "No",
      sideLabel(line.side),
      line.caseNumber,
      line.side === "wholesale" ? "" : (line.roNumber ?? roLabel(null)),
      line.customerName,
      line.side === "wholesale" ? "" : vehicleLabel(line),
      line.vinEnding,
      line.advisorName,
      line.technicianName,
      etaLabel(line.eta),
      line.vendorOrderReference ?? "",
      line.customerReference ?? "",
      line.rrNumber ?? "",
    ]),
  );
  return `\uFEFF${csvRow(CSV_HEADER)}${rows.join("")}`;
}

export function csvFileName(range: ResolvedRange): string {
  return `stock-orders-${range.from}-${range.to}.csv`;
}

/**
 * Hands the browser a file to save. The anchor is never attached to the
 * document, so nothing on the page changes; the object URL is released a
 * moment after the click, once the browser has begun reading it (revoking
 * it synchronously can cancel the download in some browsers).
 */
export function downloadTextFile(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
